import React from 'react';
import type {
  GetIntervalProps,
  GetIntervalPropsParams,
  IntervalRenderer,
} from './Interval';
import Interval from './Interval';
import type { Moment } from 'moment/moment';
import type { Unit } from '../Timeline';
import type { CustomHeaderRendererProps } from './CustomHeader';

export type CustomDateHeaderPropsData<Data = object> = {
  style?: React.CSSProperties | undefined;
  className?: string | undefined;
  unitProp?: Unit | 'primaryHeader' | undefined;
  getLabelFormat: (
    interval: [timeStart: Moment, timeEnd: Moment],
    unit: Unit,
    labelWidth: number,
  ) => string;
  intervalRenderer?:
    | React.ComponentType<IntervalRenderer<CustomDateHeaderPropsData>>
    | undefined;
  headerData?: Data | undefined;
  children?:
    | ((props: {
        getRootProps: (propsToOverride?: { style?: React.CSSProperties }) => {
          style: React.CSSProperties;
        };
        data: Data;
      }) => React.ReactNode)
    | undefined;
};

export type CustomDateHeaderProps<Data> = {
  headerContext: CustomHeaderRendererProps<
    CustomDateHeaderPropsData<Data>
  >['headerContext'];
  getIntervalProps: (props?: GetIntervalPropsParams) => GetIntervalProps;
  getRootProps: (propsToOverride?: { style?: React.CSSProperties }) => {
    style: React.CSSProperties;
  };
  showPeriod: (startDate: Moment | number, endDate: Moment | number) => void;
  data: CustomDateHeaderPropsData;
};

export function CustomDateHeader<Data>({
  headerContext: { intervals, unit },
  getRootProps,
  getIntervalProps,
  showPeriod,
  data: {
    style,
    intervalRenderer,
    className,
    getLabelFormat,
    unitProp,
    headerData,
  },
}: CustomDateHeaderProps<Data>) {
  const firstInterval = intervals.at(0);
  const width = firstInterval?.labelWidth ?? 0;
  return (
    <div
      data-testid={`dateHeader`}
      className={className}
      {...getRootProps(style && { style })}
    >
      {unit === 'day' && width < 7
        ? null
        : unit === 'isoWeek' && width < 20
          ? null
          : intervals.map((interval) => {
              const intervalText = getLabelFormat(
                [interval.startTime, interval.endTime],
                unit,
                interval.labelWidth,
              );
              return (
                <Interval
                  key={`label-${interval.startTime.valueOf()}`}
                  unit={unit}
                  interval={interval}
                  showPeriod={showPeriod}
                  intervalText={intervalText}
                  primaryHeader={unitProp === 'primaryHeader'}
                  getIntervalProps={getIntervalProps}
                  intervalRenderer={intervalRenderer}
                  headerData={headerData}
                />
              );
            })}
    </div>
  );
}
